// Canonical formatters (audit fix #L29).
//
// Stewardship OS had ~12 ad-hoc `formatDate` / `formatCurrency` /
// `formatRelativeDate` helpers spread across pages with slightly
// different signatures (null-tolerant or not), different fallback
// strings ("—" vs "(none)" vs ""), and different locale options.
// This module is the single source of truth; the Service Records
// pages migrated first (PRs 11-26 surface area).
//
// All formatters are pure + side-effect-free; safe to import from
// any page or component.

const DASH = "—";

// Date-ONLY values ("2022-03-15" — birthdays, client-since, policy
// expirations, cycle anchors) are calendar dates, not instants.
// `new Date("2022-03-15")` parses as UTC midnight, so any US timezone
// rendered it as the PREVIOUS day ("Mar 14, 2022") — the 2026-06-09
// audit found renewal/close/anniversary dates displayed one day early
// across the app (finding 3.1). Parsing the parts into a LOCAL date
// keeps the calendar date intact. Full ISO timestamps still parse
// normally — those are real instants and should render in local time.
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse an ISO string into a Date, treating date-only values
 * ("YYYY-MM-DD") as local calendar dates rather than UTC midnight.
 * Returns null for unparseable input. Exported so ad-hoc date math
 * (year extraction, age/tenure calcs) can share the same rule.
 */
export function parseDateLike(iso: string): Date | null {
  const m = DATE_ONLY_RE.exec(iso);
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format an ISO timestamp or date-only string as a short date —
 * "May 27, 2026". Returns "—" for null / undefined / unparseable
 * values so consumers don't need branching at the call site.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = parseDateLike(iso);
  if (!d) return DASH;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format an ISO timestamp as a short date + time — "May 27, 2026,
 * 5:42 PM". Used by automation surfaces that need timestamp
 * precision (run history, kill switches, version history).
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = parseDateLike(iso);
  if (!d) return DASH;
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format a USD amount as a whole-dollar string ($45,000). Returns
 * "—" for null / undefined.
 */
export function formatCurrency(
  n: number | null | undefined,
): string {
  if (n === null || n === undefined) return DASH;
  if (!Number.isFinite(n)) return DASH;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/**
 * Format an ISO timestamp as a relative-ago string — "5m ago",
 * "3h ago", "2d ago", or fall back to the absolute date for
 * anything older than 30 days. Returns "never" for null inputs so
 * "last run at" surfaces stay readable.
 */
export function formatRelativeDate(
  iso: string | null | undefined,
): string {
  if (!iso) return "never";
  const d = parseDateLike(iso);
  if (!d) return iso;
  const ms = Date.now() - d.getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}
